import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography, Box, Paper, IconButton, Tooltip, Switch, Fab,
  CircularProgress, Modal, Button, Chip, Table, TableHead,
  TableBody, TableRow, TableCell, TableSortLabel,
} from "@mui/material";
import { useNotification } from '../context/NotificationContext';
import EditNoteIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { useAuth } from '../context/AuthContext';
import { useScheduledSearches, useToggleScheduledSearch, useDeleteScheduledSearch } from '../hooks/useScheduledSearches';

const formatDate = (isoString) => {
  if (!isoString) return '—';
  try {
    return new Date(isoString).toLocaleString('hr-HR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return isoString;
  }
};

const sortRows = (rows, key, dir) =>
  [...rows].sort((a, b) => {
    let av, bv;
    switch (key) {
      case 'name': av = a.name ?? ''; bv = b.name ?? ''; break;
      case 'terms': av = (a.terms?.length ?? 0); bv = (b.terms?.length ?? 0); break;
      case 'portals': av = (a.portals?.length ?? 0); bv = (b.portals?.length ?? 0); break;
      case 'last_run': av = a.timestamp_last_run ?? ''; bv = b.timestamp_last_run ?? ''; break;
      case 'is_active': av = a.is_active ? 1 : 0; bv = b.is_active ? 1 : 0; break;
      default: av = ''; bv = '';
    }
    if (typeof av === 'string') { av = av.toLowerCase(); bv = bv.toLowerCase(); }
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return dir === 'asc' ? cmp : -cmp;
  });

const toggleDir = (prev, key) => ({
  key,
  dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
});

const ScheduledSearch = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.user_id;
  const { showSuccess, showError } = useNotification();

  const ssLimit = user?.scheduled_searches_limit ?? 0;
  const { data: searches = [], isLoading } = useScheduledSearches(userId);
  const toggleSearch = useToggleScheduledSearch();
  const deleteSearch = useDeleteScheduledSearch();

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });

  useEffect(() => { document.title = 'Automatizirane pretrage | Media Scanner'; }, []);

  const handleDetails = (id) => {
    navigate(`/scheduled-search-details/${id}`);
  };

  const handleToggle = (search) => {
    toggleSearch.mutate(
      { id: search.id, is_active: !search.is_active, user_id: userId },
      {
        onSuccess: () => showSuccess(search.is_active ? 'Pretraga deaktivirana' : 'Pretraga aktivirana'),
        onError: () => showError('Greška pri promjeni statusa pretrage'),
      }
    );
  };

  const handleDelete = (id) => {
    deleteSearch.mutate({ id, user_id: userId }, {
      onSuccess: () => {
        setConfirmDeleteOpen(false);
        setDeleteTargetId(null);
      }
    });
  };

  const sortProps = (key) => ({
    active: sort.key === key,
    direction: sort.key === key ? sort.dir : 'asc',
    onClick: () => setSort(prev => toggleDir(prev, key)),
  });

  const sortedSearches = sortRows(searches, sort.key, sort.dir);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: 2, py: 3 }}>
        {/* Page header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50' }}>
            Automatizirane pretrage
          </Typography>
          {!isLoading && (
            <Chip
              label={`${searches.length} / ${ssLimit}`}
              size="small"
              sx={{ fontWeight: 600, backgroundColor: '#e3f2fd', color: '#0079bf' }}
            />
          )}
        </Box>

        {/* Table */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : searches.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <SearchOffIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              Nema postavljenih automatiziranih pretraga.
            </Typography>
            {searches.length < ssLimit && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                sx={{ mt: 2 }}
                onClick={() => navigate("/scheduledSearchCreate")}
              >
                Kreiraj prvu pretragu
              </Button>
            )}
          </Paper>
        ) : (
          <Paper sx={{ overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <TableSortLabel {...sortProps('name')}>Naziv</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <TableSortLabel {...sortProps('terms')}>Pojmovi</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <TableSortLabel {...sortProps('portals')}>Portali</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <TableSortLabel {...sortProps('last_run')}>Zadnje pokretanje</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <TableSortLabel {...sortProps('is_active')}>Status</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 100 }} align="right">Akcije</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedSearches.map((search) => (
                  <TableRow key={search.id} hover>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: '#0079bf',
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                        onClick={() => handleDetails(search.id)}
                      >
                        {search.name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, color: '#555', maxWidth: 250 }}>
                      {Array.isArray(search.terms) && search.terms.length
                        ? search.terms.map(t => t.name ?? t.text ?? '').filter(Boolean).join(', ')
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {Array.isArray(search.portals) && search.portals.length > 0
                        ? <Chip label={search.portals.length} size="small" color="primary" variant="outlined" />
                        : <Typography variant="body2" color="text.secondary">—</Typography>}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                      {formatDate(search.timestamp_last_run)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            px: 1, py: 0.25, borderRadius: 1, fontWeight: 600,
                            backgroundColor: search.is_active ? '#e8f5e9' : '#f5f5f5',
                            color: search.is_active ? '#27ae60' : '#999',
                          }}
                        >
                          {search.is_active ? 'Aktivna' : 'Neaktivna'}
                        </Typography>
                        <Tooltip title={search.is_active ? "Deaktiviraj" : "Aktiviraj"}>
                          <Switch
                            size="small"
                            checked={search.is_active}
                            onChange={() => handleToggle(search)}
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Uredi">
                        <IconButton size="small" onClick={() => handleDetails(search.id)}>
                          <EditNoteIcon sx={{ color: '#0079bf', fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Obriši">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setDeleteTargetId(search.id);
                            setConfirmDeleteOpen(true);
                          }}
                        >
                          <DeleteIcon sx={{ color: '#e74c3c', fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Box>

      {/* FAB */}
      {searches.length < ssLimit && (
        <Fab
          color="primary"
          aria-label="Dodaj pretragu"
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            boxShadow: 3,
            zIndex: 1000
          }}
          onClick={() => navigate("/scheduledSearchCreate")}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Delete confirmation modal */}
      <Modal
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        aria-labelledby="confirm-delete-title"
      >
        <Paper
          elevation={8}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 420,
            borderRadius: 2,
            p: 4,
          }}
        >
          <Typography id="confirm-delete-title" variant="h6" sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>
            Brisanje pretrage
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Jeste li sigurni da želite izbrisati ovu automatiziranu pretragu? Ova akcija se ne može poništiti.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setConfirmDeleteOpen(false);
                setDeleteTargetId(null);
              }}
            >
              Odustani
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                if (deleteTargetId !== null) {
                  handleDelete(deleteTargetId);
                }
              }}
            >
              Izbriši
            </Button>
          </Box>
        </Paper>
      </Modal>
    </Box>
  );
};

export default ScheduledSearch;
